import { useQuery } from "@tanstack/react-query";

/**
 * API client for TradeMedia platform leaderboard data
 * Fetches data from the public API endpoints with rate limiting and caching
 */

// Use environment variable or default to production URL
const PLATFORM_API_URL = process.env.NEXT_PUBLIC_PLATFORM_API_URL || "https://trademania.io";

// Types matching the API response
export type ExchangeStats = {
  name: string;
  totalPrize: number;
  totalWinners: number;
  usersCount: number;
};

export type TopUser = {
  name: string;
  avatarURL: string;
  tradingVolume: number;
  cashback: number;
  position: number;
};

export type MixedTopUser = {
  name: string;
  avatarURL: string;
  tradingVolume: number;
  cashback: number;
  position: number;
  exchange: string;
  exchangeColor: string;
};

/**
 * Fetch exchange statistics from the platform
 */
async function fetchExchangeStats(): Promise<ExchangeStats[]> {
  const response = await fetch(`${PLATFORM_API_URL}/api/public/leaderboard/stats`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    // Enable caching at the fetch level
    next: { revalidate: 300 }, // 5 minutes
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch exchange stats: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return data.data;
}

/**
 * Fetch top users for a specific exchange
 */
async function fetchTopUsers(exchange: string, limit: number = 5): Promise<TopUser[]> {
  const response = await fetch(
    `${PLATFORM_API_URL}/api/public/leaderboard/top-users?exchange=${exchange}&limit=${limit}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      // Enable caching at the fetch level
      next: { revalidate: 300 }, // 5 minutes
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch top users for ${exchange}: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return data.data;
}

/**
 * Fetch mixed top users from all exchanges (interleaved)
 */
async function fetchMixedTopUsers(limit: number = 20): Promise<MixedTopUser[]> {
  const response = await fetch(
    `${PLATFORM_API_URL}/api/public/leaderboard/top-mixed?limit=${limit}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      // Enable caching at the fetch level
      next: { revalidate: 300 }, // 5 minutes
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch mixed top users: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return data.data;
}

/**
 * React Query hook to fetch exchange statistics
 * Automatically handles caching, refetching, and loading states
 */
export function useExchangeStats() {
  return useQuery({
    queryKey: ["exchangeStats"],
    queryFn: fetchExchangeStats,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * React Query hook to fetch top users for a specific exchange
 * @param exchange - Exchange name (BITUNIX, PIONEX, PHEMEX)
 * @param limit - Number of top users to fetch (default: 5)
 */
export function useTopUsers(exchange: string, limit: number = 5) {
  return useQuery({
    queryKey: ["topUsers", exchange, limit],
    queryFn: () => fetchTopUsers(exchange, limit),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!exchange, // Only run if exchange is provided
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * React Query hook to fetch top users for all exchanges
 * Returns a map of exchange names to their top users
 */
export function useAllTopUsers(limit: number = 5) {
  const exchanges = ["BITUNIX", "PIONEX", "PHEMEX"];

  const queries = exchanges.map((exchange) => ({
    queryKey: ["topUsers", exchange, limit],
    queryFn: () => fetchTopUsers(exchange, limit),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  }));

  // You can use useQueries for parallel fetching
  // For now, return individual query hooks
  return {
    bitunix: useQuery(queries[0]),
    pionex: useQuery(queries[1]),
    phemex: useQuery(queries[2]),
    // bitget: useQuery(queries[3]),
  };
}

/**
 * React Query hook to fetch mixed top users from all exchanges
 * Returns users interleaved from different exchanges to create variety
 * Perfect for hero sliders and showcase sections
 * @param limit - Number of mixed users to fetch (default: 20)
 */
export function useMixedTopUsers(limit: number = 20) {
  return useQuery({
    queryKey: ["mixedTopUsers", limit],
    queryFn: () => fetchMixedTopUsers(limit),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}
